import React, { useEffect, useMemo, useState } from "react";
import { Sidebar } from "primereact/sidebar";
import {
  ConfirmDialog,
  confirmDialog,
} from "primereact/confirmdialog";
import { toast } from "react-toastify";

import api from "../api/axios";
import TablePagination from "../components/TablePagination";

import "../assets/styles/page.css";
import "../assets/styles/collections.css";

import {
  TbPlus,
  TbEdit,
  TbTrash,
  TbEyeSearch,
  TbFolderHeart,
  TbPackage,
  TbSearch,
  TbX,
  TbRefresh,
  TbCircleCheck,
  TbCircleX,
  TbGripVertical,
  TbChevronUp,
  TbChevronDown,
} from "react-icons/tb";

const PAGE_NAME = "Collections";
const PRODUCT_PICKER_PAGE_SIZE = 8;
const COLLECTION_PRODUCTS_PAGE_SIZE = 8;

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const formatDate = (value) => {
  if (!value) return "—";

  const date = new Date(value);

  return Number.isNaN(date.getTime())
    ? "—"
    : date.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
};

const getPermissionHeaders = (action) => ({
  "x-page-name": PAGE_NAME,
  "x-action": action,
});

const showRequestError = (error, fallbackMessage) => {
  // The Axios interceptor handles the global 403 popup.
  if (error.response?.status === 403) return;

  toast.error(
    error.response?.data?.message || fallbackMessage
  );
};

const Collections = () => {
  const [collections, setCollections] = useState([]);
  const [products, setProducts] = useState([]);

  const [loading, setLoading] = useState(false);
  const [productsLoading, setProductsLoading] =
    useState(false);
  const [savingCollection, setSavingCollection] =
    useState(false);
  const [savingProducts, setSavingProducts] =
    useState(false);

  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [statusUpdatingId, setStatusUpdatingId] =
    useState(null);
  const [productSearch, setProductSearch] =
    useState("");
  const [detailProductSearch, setDetailProductSearch] =
    useState("");
  const [productPage, setProductPage] = useState(1);
  const [detailProductPage, setDetailProductPage] = useState(1);
  const [productPagination, setProductPagination] = useState({
    page: 1,
    total: 0,
    totalPages: 1,
  });
  const [draggedProductId, setDraggedProductId] = useState(null);
  const [reorderingProducts, setReorderingProducts] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] =
    useState(false);
  const [productPickerOpen, setProductPickerOpen] =
    useState(false);

  const [selectedCollection, setSelectedCollection] =
    useState(null);

  const [form, setForm] = useState({
    name: "",
    description: "",
    status: "active",
    sort_order: 0,
  });

  const [
    selectedProductIds,
    setSelectedProductIds,
  ] = useState([]);

  const fetchCollections = async () => {
    try {
      setLoading(true);

      const response = await api.get(
        "/admin/products/collections",
        {
          headers: getPermissionHeaders("view"),
          params: {
            search: search || undefined,
          },
        }
      );

      setCollections(response.data?.data || []);
    } catch (error) {
      showRequestError(
        error,
        "Failed to fetch collections."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchProducts = async (page = productPage) => {
    if (!selectedCollection?.collection_id) return;

    try {
      setProductsLoading(true);

      const response = await api.get(
        "/admin/products/collections-products/selection",
        {
          headers: getPermissionHeaders("view"),
          params: {
            page,
            limit: PRODUCT_PICKER_PAGE_SIZE,
            collection_id:
              selectedCollection.collection_id,
            search: productSearch || undefined,
          },
        }
      );

      setProducts(response.data?.data || []);
      setProductPagination(
        response.data?.pagination || {
          page,
          total: 0,
          totalPages: 1,
        }
      );
    } catch (error) {
      showRequestError(
        error,
        "Failed to fetch products."
      );
    } finally {
      setProductsLoading(false);
    }
  };

  const fetchCollectionDetails = async (
    collectionId
  ) => {
    try {
      const response = await api.get(
        `/admin/products/collections/${collectionId}`,
        {
          headers: getPermissionHeaders("view"),
        }
      );

      return response.data?.data || null;
    } catch (error) {
      showRequestError(
        error,
        "Failed to fetch collection."
      );

      return null;
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCollections();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    if (!productPickerOpen) return undefined;

    const timer = setTimeout(() => {
      fetchProducts();
    }, 300);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    productPickerOpen,
    productSearch,
    productPage,
    selectedCollection?.collection_id,
  ]);

  const resetForm = () => {
    setForm({
      name: "",
      description: "",
      status: "active",
      sort_order: 0,
    });

    setSelectedCollection(null);
  };

  const closeForm = () => {
    setFormOpen(false);
    resetForm();
  };

  const openCreate = () => {
    resetForm();
    setFormOpen(true);
  };

  const openEdit = (collection) => {
    setSelectedCollection(collection);

    setForm({
      name: collection.name || "",
      description: collection.description || "",
      status: collection.status || "active",
      sort_order: Number(
        collection.sort_order || 0
      ),
    });

    setFormOpen(true);
  };

  const openDetails = async (collection) => {
    const details = await fetchCollectionDetails(
      collection.collection_id
    );

    if (!details) return;

    setDetailProductSearch("");
    setDetailProductPage(1);
    setSelectedCollection(details);
    setDetailsOpen(true);
  };

  const refreshSelectedCollection = async () => {
    if (!selectedCollection?.collection_id) return;

    const updated = await fetchCollectionDetails(
      selectedCollection.collection_id
    );

    if (updated) {
      setSelectedCollection(updated);
    }
  };

  const saveCollection = async () => {
    const cleanName = form.name.trim();

    if (!cleanName) {
      toast.error("Collection name is required.");
      return;
    }

    try {
      setSavingCollection(true);

      const payload = {
        name: cleanName,
        description: form.description.trim(),
        status: form.status,
        sort_order: Number(form.sort_order || 0),
      };

      if (selectedCollection?.collection_id) {
        await api.put(
          `/admin/products/collections/${selectedCollection.collection_id}`,
          payload,
          {
            headers: getPermissionHeaders("edit"),
          }
        );

        toast.success(
          "Collection updated successfully."
        );
      } else {
        await api.post(
          "/admin/products/collections",
          payload,
          {
            headers: getPermissionHeaders("add"),
          }
        );

        toast.success(
          "Collection created successfully."
        );
      }

      closeForm();
      await fetchCollections();
    } catch (error) {
      showRequestError(
        error,
        "Collection save failed."
      );
    } finally {
      setSavingCollection(false);
    }
  };

  const deleteCollection = (collection) => {
    confirmDialog({
      header: "Delete Collection",
      message: `Delete "${collection.name}"? Products will not be deleted.`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",

      accept: async () => {
        try {
          await api.delete(
            `/admin/products/collections/${collection.collection_id}`,
            {
              headers:
                getPermissionHeaders("delete"),
            }
          );

          toast.success("Collection deleted.");

          if (
            Number(
              selectedCollection?.collection_id
            ) === Number(collection.collection_id)
          ) {
            setDetailsOpen(false);
            setProductPickerOpen(false);
            setSelectedCollection(null);
          }

          await fetchCollections();
        } catch (error) {
          showRequestError(
            error,
            "Delete failed."
          );
        }
      },
    });
  };

  const updateCollectionStatus = async (
    collection,
    nextStatus
  ) => {
    if (nextStatus === collection.status) return;

    try {
      setStatusUpdatingId(collection.collection_id);

      await api.put(
        `/admin/products/collections/${collection.collection_id}`,
        { status: nextStatus },
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      setCollections((previous) =>
        previous.map((item) =>
          item.collection_id === collection.collection_id
            ? { ...item, status: nextStatus }
            : item
        )
      );

      toast.success(
        `"${collection.name}" is now ${nextStatus}.`
      );
    } catch (error) {
      showRequestError(
        error,
        "Status update failed."
      );
    } finally {
      setStatusUpdatingId(null);
    }
  };

  const openProductPicker = () => {
    if (!selectedCollection?.collection_id) {
      toast.error(
        "Collection details are unavailable."
      );
      return;
    }

    const currentIds = (
      selectedCollection.products || []
    ).map((product) =>
      Number(product.product_id)
    );

    setSelectedProductIds(currentIds);
    setProductSearch("");
    setProductPage(1);
    setProductPagination({
      page: 1,
      total: 0,
      totalPages: 1,
    });
    setProductPickerOpen(true);
  };

  const toggleSelectedProduct = (productId) => {
    const numericProductId = Number(productId);

    setSelectedProductIds((previous) =>
      previous.includes(numericProductId)
        ? previous.filter(
            (id) => id !== numericProductId
          )
        : [...previous, numericProductId]
    );
  };

  const saveCollectionProducts = async () => {
    if (!selectedCollection?.collection_id) return;

    try {
      setSavingProducts(true);

      const existingProducts =
        selectedCollection.products || [];

      const productsPayload =
        selectedProductIds.map(
          (productId, index) => {
            const existing =
              existingProducts.find(
                (item) =>
                  Number(item.product_id) ===
                  Number(productId)
              );

            return {
              product_id: productId,

              sort_order:
                existing?.ProductCollection
                  ?.sort_order !== undefined
                  ? Number(
                      existing.ProductCollection
                        .sort_order
                    )
                  : index,

              is_visible:
                existing?.ProductCollection
                  ?.is_visible !== undefined
                  ? Boolean(
                      existing.ProductCollection
                        .is_visible
                    )
                  : true,
            };
          }
        );

      await api.put(
        `/admin/products/collections/${selectedCollection.collection_id}/products`,
        {
          products: productsPayload,
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      toast.success(
        "Collection products updated."
      );

      const updated =
        await fetchCollectionDetails(
          selectedCollection.collection_id
        );

      if (updated) {
        setSelectedCollection(updated);
      }

      setProductPickerOpen(false);
      await fetchCollections();
    } catch (error) {
      showRequestError(
        error,
        "Failed to update products."
      );
    } finally {
      setSavingProducts(false);
    }
  };

  const removeProductFromCollection = (
    product
  ) => {
    if (!selectedCollection?.collection_id) return;

    confirmDialog({
      header: "Remove Product",
      message: `Remove "${getProductName(
        product
      )}" from this collection?`,
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",

      accept: async () => {
        try {
          await api.delete(
            `/admin/products/collections/${selectedCollection.collection_id}/products/${product.product_id}`,
            {
              // This edits collection membership.
              headers:
                getPermissionHeaders("edit"),
            }
          );

          toast.success(
            "Product removed from collection."
          );

          const updated =
            await fetchCollectionDetails(
              selectedCollection.collection_id
            );

          if (updated) {
            setSelectedCollection(updated);
          }

          await fetchCollections();
        } catch (error) {
          showRequestError(
            error,
            "Remove failed."
          );
        }
      },
    });
  };

  const toggleProductVisibility = async (
    product
  ) => {
    if (!selectedCollection?.collection_id) return;

    try {
      const currentVisible = Boolean(
        product.ProductCollection?.is_visible
      );

      await api.patch(
        `/admin/products/collections/${selectedCollection.collection_id}/products/${product.product_id}/visibility`,
        {
          is_visible: !currentVisible,
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      toast.success("Visibility updated.");

      const updated =
        await fetchCollectionDetails(
          selectedCollection.collection_id
        );

      if (updated) {
        setSelectedCollection(updated);
      }
    } catch (error) {
      showRequestError(
        error,
        "Visibility update failed."
      );
    }
  };

  const persistProductOrder = async (orderedProducts, previousProducts) => {
    if (!selectedCollection?.collection_id || reorderingProducts) return;

    const normalisedProducts = orderedProducts.map((product, index) => ({
      ...product,
      ProductCollection: {
        ...(product.ProductCollection || {}),
        sort_order: index,
      },
    }));

    setSelectedCollection((current) => ({
      ...current,
      products: normalisedProducts,
    }));

    try {
      setReorderingProducts(true);

      await api.patch(
        `/admin/products/collections/${selectedCollection.collection_id}/products/sort`,
        {
          products: normalisedProducts.map((product, index) => ({
            product_id: product.product_id,
            sort_order: index,
          })),
        },
        {
          headers: getPermissionHeaders("edit"),
        }
      );

      toast.success("Product order updated.");
    } catch (error) {
      setSelectedCollection((current) => ({
        ...current,
        products: previousProducts,
      }));
      showRequestError(error, "Sort update failed.");
    } finally {
      setReorderingProducts(false);
      setDraggedProductId(null);
    }
  };

  const moveCollectionProduct = (productId, direction) => {
    if (reorderingProducts) return;

    const currentProducts = [...selectedCollectionProducts];
    const currentIndex = currentProducts.findIndex(
      (product) => Number(product.product_id) === Number(productId)
    );
    const nextIndex = currentIndex + direction;

    if (
      currentIndex < 0 ||
      nextIndex < 0 ||
      nextIndex >= currentProducts.length
    ) {
      return;
    }

    const reordered = [...currentProducts];
    const [movedProduct] = reordered.splice(currentIndex, 1);
    reordered.splice(nextIndex, 0, movedProduct);
    persistProductOrder(reordered, currentProducts);
  };

  const dropCollectionProduct = (targetProductId) => {
    if (!draggedProductId || reorderingProducts) return;

    const currentProducts = [...selectedCollectionProducts];
    const sourceIndex = currentProducts.findIndex(
      (product) => Number(product.product_id) === Number(draggedProductId)
    );
    const targetIndex = currentProducts.findIndex(
      (product) => Number(product.product_id) === Number(targetProductId)
    );

    if (sourceIndex < 0 || targetIndex < 0 || sourceIndex === targetIndex) {
      setDraggedProductId(null);
      return;
    }

    const reordered = [...currentProducts];
    const [movedProduct] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, movedProduct);
    persistProductOrder(reordered, currentProducts);
  };

  const getImages = (product) => {
    return (product.media || []).filter(
      (item) =>
        !item.media_type ||
        item.media_type === "image"
    );
  };

  const getPrimaryImage = (product) => {
    const images = getImages(product);

    return (
      images.find((image) => image.is_primary)
        ?.image_url ||
      images[0]?.image_url ||
      "/img/noimage.jpg"
    );
  };

  const getProductName = (product) => {
    return (
      product.product_name ||
      product.name ||
      "Unnamed Product"
    );
  };

  const getProductPrice = (product) => {
    return Number(
      product.final_price ||
        product.base_price ||
        0
    );
  };

  const selectedCollectionProducts =
    useMemo(() => {
      return [
        ...(selectedCollection?.products || []),
      ].sort((first, second) => {
        return (
          Number(
            first.ProductCollection
              ?.sort_order || 0
          ) -
          Number(
            second.ProductCollection
              ?.sort_order || 0
          )
        );
      });
    }, [selectedCollection]);

  const filteredCollectionProducts = useMemo(() => {
    const term = detailProductSearch.trim().toLowerCase();

    if (!term) return selectedCollectionProducts;

    return selectedCollectionProducts.filter((product) => {
      const searchable = [
        getProductName(product),
        product.sku,
        product.category?.name,
        product.sub_category?.name,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(term);
    });
  }, [detailProductSearch, selectedCollectionProducts]);

  const detailTotalPages = Math.max(
    1,
    Math.ceil(
      filteredCollectionProducts.length / COLLECTION_PRODUCTS_PAGE_SIZE
    )
  );

  const visibleCollectionProducts = filteredCollectionProducts.slice(
    (detailProductPage - 1) * COLLECTION_PRODUCTS_PAGE_SIZE,
    detailProductPage * COLLECTION_PRODUCTS_PAGE_SIZE
  );

  useEffect(() => {
    if (detailProductPage > detailTotalPages) {
      setDetailProductPage(detailTotalPages);
    }
  }, [detailProductPage, detailTotalPages]);

  const statusCounts = useMemo(
    () =>
      collections.reduce(
        (counts, collection) => {
          counts[""] += 1;
          counts[collection.status] =
            (counts[collection.status] || 0) + 1;
          return counts;
        },
        { "": 0, active: 0, inactive: 0 }
      ),
    [collections]
  );

  const filteredCollections = useMemo(
    () =>
      statusFilter
        ? collections.filter(
            (collection) =>
              collection.status === statusFilter
          )
        : collections,
    [collections, statusFilter]
  );

  const totalPages = Math.max(
    1,
    Math.ceil(filteredCollections.length / pageSize)
  );

  const pagedCollections = filteredCollections.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, pageSize]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const hasFilters = Boolean(search || statusFilter);

  return (
    <section className="admin-page premium-users-page collection-page">
      <ConfirmDialog />

      <div className="page-header users-hero-header collection-hero">
        <div>
          <p>Product Curation</p>
          <h1>Collections</h1>
        </div>

        <div className="page-header-actions">
          <div className="page-total-summary">
            <span>Total:</span>
            <strong>{statusCounts[""]}</strong>
          </div>

          <button
            className="primary-action"
            type="button"
            onClick={openCreate}
          >
            <TbPlus />
            Create Collection
          </button>
        </div>
      </div>

      <div className="page-card dt-card">
        <div className="dt-toolbar">
          <div className="dt-toolbar-title">
            <h2>Collection Manager</h2>
            <p>
              Group products into curated sections for the
              store. Click a collection to manage its
              products.
            </p>
          </div>

          <div className="dt-filters">
            <div
              className="dt-tabs"
              role="tablist"
              aria-label="Filter by status"
            >
              {STATUS_TABS.map((tab) => (
                <button
                  key={tab.value || "all"}
                  type="button"
                  role="tab"
                  aria-selected={statusFilter === tab.value}
                  className={
                    statusFilter === tab.value
                      ? "is-active"
                      : ""
                  }
                  onClick={() => setStatusFilter(tab.value)}
                >
                  {tab.label}
                  <em>{statusCounts[tab.value] || 0}</em>
                </button>
              ))}
            </div>

            <div className="dt-search">
              <TbSearch />
              <input
                value={search}
                onChange={(event) =>
                  setSearch(event.target.value)
                }
                placeholder="Search collections…"
                aria-label="Search collections"
              />
              {search && (
                <button
                  type="button"
                  className="dt-search-clear"
                  onClick={() => setSearch("")}
                  aria-label="Clear search"
                >
                  <TbX />
                </button>
              )}
            </div>
          </div>
        </div>

        <div className="dt-scroll">
          <table className="dt-table collection-table">
            <thead>
              <tr>
                <th className="dt-num" title="Display order in the store">
                  Order
                </th>
                <th>Collection</th>
                <th>Description</th>
                <th>Products</th>
                <th>Status</th>
                <th>Last updated</th>
                <th className="dt-right">Actions</th>
              </tr>
            </thead>

            <tbody>
              {loading &&
                collections.length === 0 &&
                Array.from({ length: 5 }, (_, index) => (
                  <tr className="dt-skeleton" key={`skeleton-${index}`}>
                    {[30, 180, 220, 90, 80, 90, 110].map(
                      (width, cell) => (
                        <td key={cell}>
                          <span
                            className="dt-skeleton-bar"
                            style={{ width }}
                          />
                        </td>
                      )
                    )}
                  </tr>
                ))}

              {!loading && filteredCollections.length === 0 && (
                <tr className="dt-empty">
                  <td colSpan="7">
                    <div className="dt-empty-inner">
                      <span className="dt-empty-icon">
                        <TbFolderHeart />
                      </span>

                      {hasFilters ? (
                        <>
                          <strong>No matching collections</strong>
                          <span>
                            Try another search term or status.
                          </span>
                          <button
                            type="button"
                            className="ghost-action bottonborder"
                            onClick={() => {
                              setSearch("");
                              setStatusFilter("");
                            }}
                          >
                            Clear filters
                          </button>
                        </>
                      ) : (
                        <>
                          <strong>No collections yet</strong>
                          <span>
                            Create a collection like “Best
                            Sellers” or “Royal Weddings”, then
                            add products to it.
                          </span>
                          <button
                            type="button"
                            className="primary-action"
                            onClick={openCreate}
                          >
                            <TbPlus />
                            Create your first collection
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              )}

              {pagedCollections.map((collection) => {
                const productCount = Number(
                  collection.product_count || 0
                );

                const hiddenCount =
                  productCount -
                  Number(collection.visible_count ?? productCount);

                return (
                  <tr key={collection.collection_id}>
                    <td className="dt-num">
                      #{collection.sort_order ?? 0}
                    </td>

                    <td>
                      <div className="dt-entity">
                        <span className="dt-entity-icon">
                          <TbFolderHeart />
                        </span>

                        <div className="dt-entity-text">
                          <button
                            type="button"
                            className="dt-entity-title"
                            onClick={() => openDetails(collection)}
                            title="Open collection"
                          >
                            {collection.name}
                          </button>
                          <span className="dt-entity-sub">
                            /{collection.slug}
                          </span>
                        </div>
                      </div>
                    </td>

                    <td>
                      {collection.description ? (
                        <span className="dt-clamp">
                          {collection.description}
                        </span>
                      ) : (
                        <span className="dt-placeholder">
                          No description
                        </span>
                      )}
                    </td>

                    <td>
                      <button
                        type="button"
                        className="dt-count-button"
                        onClick={() => openDetails(collection)}
                        title="Manage products in this collection"
                      >
                        <TbPackage />
                        {productCount}{" "}
                        {productCount === 1 ? "product" : "products"}
                        {hiddenCount > 0 && (
                          <small>· {hiddenCount} hidden</small>
                        )}
                      </button>
                    </td>

                    <td>
                      <span
                        className={`dt-status is-${collection.status}`}
                      >
                        <select
                          value={collection.status}
                          disabled={
                            statusUpdatingId ===
                            collection.collection_id
                          }
                          aria-label={`Status of ${collection.name}`}
                          onChange={(event) =>
                            updateCollectionStatus(
                              collection,
                              event.target.value
                            )
                          }
                        >
                          <option value="active">Active</option>
                          <option value="inactive">Inactive</option>
                        </select>
                      </span>
                    </td>

                    <td>
                      <div className="dt-stack">
                        <strong>
                          {formatDate(
                            collection.updatedAt ||
                              collection.createdAt
                          )}
                        </strong>
                        <span>
                          Created {formatDate(collection.createdAt)}
                        </span>
                      </div>
                    </td>

                    <td>
                      <div className="dt-actions">
                        <button
                          type="button"
                          onClick={() => openDetails(collection)}
                          title="View & manage products"
                          aria-label={`View ${collection.name}`}
                        >
                          <TbEyeSearch />
                        </button>

                        <button
                          type="button"
                          onClick={() => openEdit(collection)}
                          title="Edit details"
                          aria-label={`Edit ${collection.name}`}
                        >
                          <TbEdit />
                        </button>

                        <button
                          type="button"
                          className="is-danger"
                          onClick={() =>
                            deleteCollection(collection)
                          }
                          title="Delete collection"
                          aria-label={`Delete ${collection.name}`}
                        >
                          <TbTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {filteredCollections.length > 0 && (
          <TablePagination
            page={page}
            totalPages={totalPages}
            total={filteredCollections.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            itemLabel="collections"
          />
        )}
      </div>

      <Sidebar
        visible={formOpen}
        position="right"
        onHide={closeForm}
        className="premium-user-sidebar collection-sidebar"
      >
        <div className="user-view-shell">
          <div className="user-view-title">
            <span>
              {selectedCollection
                ? "Edit Collection"
                : "New Collection"}
            </span>

            <h2>
              {selectedCollection
                ? selectedCollection.name
                : "Create Collection"}
            </h2>

            <p>
              Build curated product groups for
              customer-facing sections.
            </p>
          </div>

          <div className="collection-form">
            <label>
              Collection Name

              <input
                value={form.name}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    name: event.target.value,
                  }))
                }
                placeholder="e.g. Best Sellers"
                required
              />
            </label>

            <label>
              Description

              <textarea
                value={form.description}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    description:
                      event.target.value,
                  }))
                }
                placeholder="Short description for internal/customer context"
              />
            </label>

            <label>
              Status

              <select
                value={form.status}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    status: event.target.value,
                  }))
                }
              >
                <option value="active">
                  Active
                </option>

                <option value="inactive">
                  Inactive
                </option>
              </select>
            </label>

            <label>
              Sort Order

              <input
                type="number"
                min="0"
                value={form.sort_order}
                onChange={(event) =>
                  setForm((previous) => ({
                    ...previous,
                    sort_order: Number(
                      event.target.value || 0
                    ),
                  }))
                }
              />
            </label>
          </div>

          <div className="user-view-actions">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={closeForm}
              disabled={savingCollection}
            >
              Cancel
            </button>

            <button
              type="button"
              className="primary-action"
              onClick={saveCollection}
              disabled={savingCollection}
            >
              {savingCollection
                ? "Saving..."
                : selectedCollection
                  ? "Update Collection"
                  : "Create Collection"}
            </button>
          </div>
        </div>
      </Sidebar>

      <Sidebar
        visible={detailsOpen}
        position="right"
        onHide={() => {
          setDetailsOpen(false);
          setProductPickerOpen(false);
        }}
        className="premium-user-sidebar collection-detail-sidebar"
      >
        {selectedCollection && (
          <div className="user-view-shell collection-detail-shell">
            <div className="user-view-title">
              <span>Collection Details</span>

              <h2>
                {selectedCollection.name}
              </h2>

              <p>
                {selectedCollection.description ||
                  "No description added."}
              </p>
            </div>

            <div className="user-view-meta">
              <div>
                <strong>Status</strong>

                <span
                  className={`status-badge ${selectedCollection.status}`}
                >
                  {selectedCollection.status}
                </span>
              </div>

              <div>
                <strong>Sort Order</strong>

                <span>
                  {selectedCollection.sort_order}
                </span>
              </div>

              <div>
                <strong>Products</strong>

                <span>
                  {
                    selectedCollectionProducts.length
                  }
                </span>
              </div>
            </div>

            <div className="collection-detail-actions">
              <button
                type="button"
                className="primary-action"
                onClick={openProductPicker}
              >
                <TbPlus />
                Select Products
              </button>

              <button
                type="button"
                className="ghost-action bottonborder"
                onClick={
                  refreshSelectedCollection
                }
              >
                <TbRefresh />
                Refresh
              </button>
            </div>

            <div className="collection-product-manager-head">
              <div>
                <strong>Products & display order</strong>
                <span>Drag rows to reorder, or use the arrow controls.</span>
              </div>
              <div className="collection-product-search">
                <TbSearch />
                <input
                  value={detailProductSearch}
                  onChange={(event) => {
                    setDetailProductSearch(event.target.value);
                    setDetailProductPage(1);
                  }}
                  placeholder="Search in collection..."
                />
              </div>
            </div>

            <div className="collection-products-list">
              {selectedCollectionProducts.length === 0 && (
                <div className="premium-empty-card compact">
                  <h3>No products added.</h3>

                  <p>
                    Select products to display
                    inside this collection.
                  </p>
                </div>
              )}

              {selectedCollectionProducts.length > 0 &&
                filteredCollectionProducts.length === 0 && (
                  <div className="premium-empty-card compact">
                    <h3>No matching products.</h3>
                    <p>Try a different product name, SKU, or category.</p>
                  </div>
                )}

              {visibleCollectionProducts.map((product) => {
                const productPosition = selectedCollectionProducts.findIndex(
                  (item) => Number(item.product_id) === Number(product.product_id)
                );

                return (
                  <div
                    className={`collection-product-row ${
                      Number(draggedProductId) === Number(product.product_id)
                        ? "dragging"
                        : ""
                    }`}
                    key={product.product_id}
                    onDragOver={(event) => event.preventDefault()}
                    onDrop={() => dropCollectionProduct(product.product_id)}
                  >
                    <button
                      type="button"
                      className="collection-drag-handle"
                      draggable={!reorderingProducts}
                      title="Drag to reorder"
                      aria-label={`Drag ${getProductName(product)} to reorder`}
                      onDragStart={() => setDraggedProductId(product.product_id)}
                      onDragEnd={() => setDraggedProductId(null)}
                    >
                      <TbGripVertical />
                    </button>

                    <img
                      src={getPrimaryImage(
                        product
                      )}
                      alt={getProductName(product)}
                      onError={(event) => {
                        event.currentTarget.src =
                          "/img/noimage.jpg";
                      }}
                    />

                    <div className="collection-product-info">
                      <strong>
                        {getProductName(product)}
                      </strong>

                      <small className="collection-product-position">
                        Position {productPosition + 1}
                      </small>

                      <small>
                        {product.category?.name ||
                          "-"}{" "}
                        /{" "}
                        {product.sub_category
                          ?.name || "-"}
                      </small>

                      <small>
                        ₹
                        {getProductPrice(
                          product
                        ).toFixed(2)}
                      </small>
                    </div>

                    <div className="collection-product-controls">
                      <div className="collection-order-buttons">
                        <button
                          type="button"
                          title="Move up"
                          aria-label={`Move ${getProductName(product)} up`}
                          disabled={productPosition === 0 || reorderingProducts}
                          onClick={() => moveCollectionProduct(product.product_id, -1)}
                        >
                          <TbChevronUp />
                        </button>
                        <button
                          type="button"
                          title="Move down"
                          aria-label={`Move ${getProductName(product)} down`}
                          disabled={
                            productPosition === selectedCollectionProducts.length - 1 ||
                            reorderingProducts
                          }
                          onClick={() => moveCollectionProduct(product.product_id, 1)}
                        >
                          <TbChevronDown />
                        </button>
                      </div>

                      <button
                        type="button"
                        className={`visibility-pill ${
                          product
                            .ProductCollection
                            ?.is_visible
                            ? "visible"
                            : "hidden"
                        }`}
                        onClick={() =>
                          toggleProductVisibility(
                            product
                          )
                        }
                      >
                        {product
                          .ProductCollection
                          ?.is_visible ? (
                          <>
                            <TbCircleCheck />
                            Visible
                          </>
                        ) : (
                          <>
                            <TbCircleX />
                            Hidden
                          </>
                        )}
                      </button>

                      <button
                        type="button"
                        className="danger-action icon-only"
                        onClick={() =>
                          removeProductFromCollection(
                            product
                          )
                        }
                        aria-label={`Remove ${getProductName(
                          product
                        )}`}
                      >
                        <TbX />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {filteredCollectionProducts.length > 0 && (
              <div className="collection-list-pagination">
                <span>
                  Showing {Math.min(
                    (detailProductPage - 1) * COLLECTION_PRODUCTS_PAGE_SIZE + 1,
                    filteredCollectionProducts.length
                  )}–{Math.min(
                    detailProductPage * COLLECTION_PRODUCTS_PAGE_SIZE,
                    filteredCollectionProducts.length
                  )} of {filteredCollectionProducts.length}
                </span>
                <div>
                  <button
                    type="button"
                    disabled={detailProductPage <= 1 || reorderingProducts}
                    onClick={() => setDetailProductPage((page) => page - 1)}
                  >
                    Previous
                  </button>
                  <strong>{detailProductPage} / {detailTotalPages}</strong>
                  <button
                    type="button"
                    disabled={
                      detailProductPage >= detailTotalPages || reorderingProducts
                    }
                    onClick={() => setDetailProductPage((page) => page + 1)}
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </Sidebar>

      <Sidebar
        visible={productPickerOpen}
        position="right"
        onHide={() =>
          setProductPickerOpen(false)
        }
        className="premium-user-sidebar product-picker-sidebar"
      >
        <div className="user-view-shell collection-picker-shell">
          <div className="user-view-title">
            <span>Select Products</span>

            <h2>
              {selectedCollection?.name}
            </h2>

            <p>
              Browse a small page at a time. Your selections stay saved while you
              search or move between pages.
            </p>
          </div>

          <div className="collection-picker-summary">
            <span><strong>{selectedProductIds.length}</strong> selected</span>
            <span><strong>{productPagination.total}</strong> products found</span>
          </div>

          <div className="premium-user-filters full">
            <div className="searchFilter">
              <TbSearch />
              <input
                value={productSearch}
                onChange={(event) => {
                  setProductSearch(event.target.value);
                  setProductPage(1);
                }}
                placeholder="Search products..."
              />
            </div>
          </div>

          <div className="product-picker-list">
            {productsLoading && (
              <p>Loading products...</p>
            )}

            {!productsLoading &&
              products.length === 0 && (
                <div className="premium-empty-card compact">
                  <h3>No products found.</h3>

                  <p>
                    Try a different search term.
                  </p>
                </div>
              )}

            {!productsLoading &&
              products.map((product) => {
                const selected =
                  selectedProductIds.includes(
                    Number(product.product_id)
                  );

                return (
                  <button
                    type="button"
                    className={`product-picker-item ${
                      selected
                        ? "selected"
                        : ""
                    }`}
                    key={product.product_id}
                    onClick={() =>
                      toggleSelectedProduct(
                        product.product_id
                      )
                    }
                  >
                    <img
                      src={getPrimaryImage(
                        product
                      )}
                      alt={getProductName(product)}
                      onError={(event) => {
                        event.currentTarget.src =
                          "/img/noimage.jpg";
                      }}
                    />

                    <span>
                      <strong>
                        {getProductName(product)}
                      </strong>

                      <small>
                        <TbPackage />

                        {product.category?.name ||
                          "-"}{" "}
                        • ₹
                        {getProductPrice(
                          product
                        ).toLocaleString(
                          "en-IN"
                        )}
                      </small>

                      {product.collection_names
                        ?.length > 0 && (
                        <small>
                          In:{" "}
                          {product.collection_names.join(
                            ", "
                          )}
                        </small>
                      )}
                    </span>

                    <em>
                      {selected
                        ? "Selected"
                        : product.is_in_current_collection
                          ? "Added"
                          : "Add"}
                    </em>
                  </button>
                );
              })}
          </div>

          <div className="collection-picker-pagination">
            <span>
              Page {productPagination.page} of {Math.max(productPagination.totalPages, 1)}
            </span>
            <div>
              <button
                type="button"
                disabled={productPage <= 1 || productsLoading}
                onClick={() => setProductPage((page) => page - 1)}
              >
                Previous
              </button>
              <button
                type="button"
                disabled={
                  productPage >= productPagination.totalPages || productsLoading
                }
                onClick={() => setProductPage((page) => page + 1)}
              >
                Next
              </button>
            </div>
          </div>

          <div className="user-view-actions collection-picker-actions">
            <button
              type="button"
              className="ghost-action bottonborder"
              onClick={() =>
                setProductPickerOpen(false)
              }
              disabled={savingProducts}
            >
              Cancel
            </button>

            <button
              type="button"
              className="primary-action"
              onClick={saveCollectionProducts}
              disabled={savingProducts}
            >
              {savingProducts
                ? "Saving..."
                : "Save Products"}
            </button>
          </div>
        </div>
      </Sidebar>
    </section>
  );
};

export default Collections;
